import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "../../components/panel/panel.jsx";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import Select from "react-select";
import APIs from "../../Constants/apis.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import moment from "moment";
import { useSelector } from "react-redux";
import CustomDateTimePicker from "../../components/DateTimePicker/dateTimePicker.js";
import CountryCitySelector from "../../components/CustomComponents/DropdownSelections/CountryCitySelector.jsx";
import CustomModal from "../../components/CustomComponents/modal/customModal.jsx";
import CoordinatePicker from "../../components/CustomComponents/Maps/CoordinatePicker.jsx";
import {
  HOSPITALITY_COMPANY_TYPES,
  isHospitalityIndustry,
} from "../../Constants/companyTypes.js";

function AddCompany() {
  const navigate = useNavigate();
  const isDarkMode = useSelector((state) => state.appSettings?.appDarkMode);
 
  const [industryTypes] = useState([
    { value: "Hospitality", label: "Hospitality" },
    { value: "Retail", label: "Retail" },
    { value: "Saloon", label: "Saloon" },
  ]);
  
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState({
    companyPin: "",
    staffPin: "",
    backOfficeUserName: "",
    backOfficePassword: "",
  });
  const [selectedIndustryType, setSelectedIndustryType] = useState(null);
  const [selectedCompType, setSelectedCompType] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [zoneOptions, setZoneOptions] = useState([]);
  const [selectedMarketplaceZone, setSelectedMarketplaceZone] = useState(null);

  const [companyData, setCompanyData] = useState({
    // From Company.cs
    compName: "",
    compDetail: "",
    compEmail: "",
    compPhone: "",
    compFax: "",
    compIndustryType: "Hospitality",
    compCurrency: "",
    compCountry: "",
    compCity: "",
    compAddress: "",
    compSecCode: "",
    compType: "",
    compRemarks: "",
    compRegDate: moment().format("YYYY-MM-DD"), // Set to current date by default
    compExpireDate: null,
    compIcon: "/img/logo.png",
    compHeaderLogo: "",
    compPNotificationLimit_PerDay: 10,
    compTimeZone: "Pakistan Standard Time",
    compUTCTimeZone: "(UTC+05:00) Islamabad, Karachi",
    compLatitude: "0",
    compLongitude: "0",
    isNeedtoSyncDB: false,
    dailyForecastBracket: false,
    isArchieve: false,
    compNumberOfDaysToShowExpiryWarning: 0,
    compRecoveryEmail: "",
    compRecoveryPhoneNumber: "",
    compLanguage: "en",
    compWebsite: "",
    compFacebooklink: "",
    compInstagramLink: "",
    compYoutubeLink: "",
    compTikTokLink: "",
    compGoogleReview: "",
    compPublish: false,
    isWebApp: false,
    allowPointOfSale: true,
    marketplace_PlatformCommissionPercent: 0,
    marketplace_PlatformCommissionFixedFee: 0,
    marketplaceZone_ID: null,

    // From Company2.cs
    parentComID: 0,
    locationLicences: 0,
    isCallerID: false,
    isMobileApp: false,
    isKitchenApp: false,
    isTVApp: false,
    isWaiterApp: false,
    compMaxIPadLicense: 1,
    waiterAppMaxDevices: 0,
    compMaxUserLicense: 1,
    // Set current date for hidden date fields to avoid DateTime.MinValue (1/1/0001) SQL errors
    mobileAppExpireDate: moment().format("YYYY-MM-DD"),
    webAppExpireDate: moment().format("YYYY-MM-DD"),
    waiterAppExpireDate: moment().format("YYYY-MM-DD"),
  });

  const zoneSelectStyles = useMemo(
    () => (isDarkMode ? DropDownStylesDark : DropDownStyles),
    [isDarkMode]
  );

  const loadMarketplaceZones = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_MARKETPLACE_ZONES, {
        params: { IsActive: true, PageNumber: 1, PageSize: 200 },
      });
      if (response.data?.status) {
        const rows = Array.isArray(response.data.data) ? response.data.data : [];
        setZoneOptions(
          rows.map((z) => ({
            value: z.zoneId || z.Zone_ID,
            label: `${z.zoneName || z.ZoneName}${z.serviceCity || z.ServiceCity ? ` (${z.serviceCity || z.ServiceCity})` : ""}`,
          }))
        );
      } else {
        setZoneOptions([]);
      }
    } catch {
      setZoneOptions([]);
    }
  }, []);

  useEffect(() => {
    loadMarketplaceZones();
  }, [loadMarketplaceZones]);

  const fetchCurrencies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_CURRENCIES);

      if (response.data && response.data.data) {
        const currencyOptions = response.data.data.map((currency) => ({
          value: currency.value,
          label: currency.text,
        }));
        setCurrencies(currencyOptions);
      } else {
        // Fallback currencies if API fails
        const fallbackCurrencies = [
          { value: "USD", label: "US Dollar" },
          { value: "EUR", label: "Euro" },
          { value: "GBP", label: "British Pound" },
          { value: "AED", label: "UAE Dirham" },
        ];
        setCurrencies(fallbackCurrencies);
      }
    } catch (err) {
      showErrorToast("Failed to load currencies");
      // Fallback currencies if API fails
      const fallbackCurrencies = [
        { value: "USD", label: "US Dollar" },
        { value: "EUR", label: "Euro" },
        { value: "GBP", label: "British Pound" },
        { value: "AED", label: "UAE Dirham" },
      ];
      setCurrencies(fallbackCurrencies);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrencies();
  }, [fetchCurrencies]);

  const handleInputChange = (e, field) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setCompanyData({ ...companyData, [field]: value });
  };

  const handleDateChange = (newValue, field) => {
    setCompanyData({
      ...companyData,
      [field]: newValue ? moment(newValue).format("YYYY-MM-DD") : null,
    });
  };

  const handleCountryCityChange = (data) => {
    setCompanyData({
      ...companyData,
      ...data,
    });
    if (data.CompCountry !== undefined) {
      setSelectedCountry(data.CompCountry);
    }
    if (data.CompCity !== undefined) {
      setSelectedCity(data.CompCity);
    }
  };

  const handleSubmit = async () => {
    const {
      compName,
      compEmail,
      compIndustryType,
      compCurrency,
    } = companyData;

    if (
      !compName ||
      !compEmail ||
      !compIndustryType ||
      !compCurrency
    ) {
      showErrorToast("Please fill in all required fields.");
      return;
    }

    if (isHospitalityIndustry(compIndustryType) && !companyData.compType) {
      showErrorToast("Please select a company type for hospitality businesses.");
      return;
    }

    try {
      setLoading(true);

      let submitCompanyData = { ...companyData };
      if (!submitCompanyData.allowPointOfSale) {
        submitCompanyData = {
          ...submitCompanyData,
          isMobileApp: false,
          isKitchenApp: false,
          isWaiterApp: false,
          isTVApp: false,
          isCallerID: false,
        };
      }
      
      // Build payload - only include date fields if they have values
      const payload = {
        // Auto-set fields
        LocalizeID: "en",

        // From Company.cs
        CompName: submitCompanyData.compName,
        CompDetail: submitCompanyData.compDetail,
        CompEmail: submitCompanyData.compEmail,
        CompPhone: submitCompanyData.compPhone,
        CompFax: submitCompanyData.compFax,
        CompIndustryType: submitCompanyData.compIndustryType,
        CompCurrency: submitCompanyData.compCurrency,
        CompCountry: submitCompanyData.CompCountry || "",
        CompCity: submitCompanyData.CompCity || "",
        CompAddress: submitCompanyData.compAddress,
        CompSecCode: submitCompanyData.compSecCode,
        CompType: submitCompanyData.compType,
        CompRemarks: submitCompanyData.compRemarks,
        CompIcon: submitCompanyData.compIcon,
        CompHeaderLogo: submitCompanyData.compHeaderLogo,
        CompPNotificationLimit_PerDay: parseInt(submitCompanyData.compPNotificationLimit_PerDay) || 10,
        CompTimeZone: submitCompanyData.compTimeZone,
        CompUTCTimeZone: submitCompanyData.compUTCTimeZone,
        CompLatitude:
          submitCompanyData.compLatitude === null ||
          submitCompanyData.compLatitude === undefined ||
          submitCompanyData.compLatitude === ""
            ? "0"
            : String(submitCompanyData.compLatitude),
        CompLongitude:
          submitCompanyData.compLongitude === null ||
          submitCompanyData.compLongitude === undefined ||
          submitCompanyData.compLongitude === ""
            ? "0"
            : String(submitCompanyData.compLongitude),
        IsNeedtoSyncDB: submitCompanyData.isNeedtoSyncDB || false,
        DailyForecastBracket: submitCompanyData.dailyForecastBracket || false,
        IsArchieve: submitCompanyData.isArchieve,
        CompNumberOfDaysToShowExpiryWarning: parseInt(submitCompanyData.compNumberOfDaysToShowExpiryWarning) || 0,
        CompRecoveryEmail: submitCompanyData.compRecoveryEmail || "",
        CompRecoveryPhoneNumber: submitCompanyData.compRecoveryPhoneNumber || "",
        CompLanguage: submitCompanyData.compLanguage,
        CompWebsite: submitCompanyData.compWebsite || "",
        CompFacebooklink: submitCompanyData.compFacebooklink || "",
        CompInstagramLink: submitCompanyData.compInstagramLink || "",
        CompYoutubeLink: submitCompanyData.compYoutubeLink || "",
        CompTikTokLink: submitCompanyData.compTikTokLink || "",
        CompGoogleReview: submitCompanyData.compGoogleReview || "",
        CompPublish: submitCompanyData.compPublish,
        AllowPointOfSale: submitCompanyData.allowPointOfSale !== false,
        Marketplace_PlatformCommissionPercent: Math.min(
          100,
          Math.max(0, parseFloat(submitCompanyData.marketplace_PlatformCommissionPercent) || 0)
        ),
        Marketplace_PlatformCommissionFixedFee: Math.max(
          0,
          parseFloat(submitCompanyData.marketplace_PlatformCommissionFixedFee) || 0
        ),
        MarketplaceZone_ID:
          submitCompanyData.marketplaceZone_ID > 0
            ? parseInt(submitCompanyData.marketplaceZone_ID, 10)
            : null,

        // From Company2.cs
        ParentComID: parseInt(submitCompanyData.parentComID) || 0,
        LocationLicences: parseInt(submitCompanyData.locationLicences) || 0,
        IsCallerID: submitCompanyData.isCallerID,
        ISMobileApp: submitCompanyData.isMobileApp,
        IsKitchenApp: submitCompanyData.isKitchenApp,
        ISWebApp: submitCompanyData.isWebApp,
        IsOnlineMarketplaceEnabled: submitCompanyData.isWebApp,
        IsTVApp: submitCompanyData.isTVApp,
        IsWaiterApp: submitCompanyData.isWaiterApp,
        CompMaxIPadLicense: parseInt(submitCompanyData.compMaxIPadLicense) || 1,
        WaiterAppMaxDevices: parseInt(submitCompanyData.waiterAppMaxDevices) || 0,
        CompMaxUserLicense: parseInt(submitCompanyData.compMaxUserLicense) || 1,
      };

      // Helper function to validate date
      const isValidDate = (dateString) => {
        if (!dateString) return false;
        const date = moment(dateString, "YYYY-MM-DD", true);
        return date.isValid() && date.year() >= 1753 && date.year() <= 9999;
      };

      // CompRegDate: Always include with current date as default (with time component for SQL)
      const regDate = companyData.compRegDate || moment().format("YYYY-MM-DD");
      if (isValidDate(regDate)) {
        payload.CompRegDate = moment(regDate).format("YYYY-MM-DDTHH:mm:ss");
      } else {
        payload.CompRegDate = moment().format("YYYY-MM-DDTHH:mm:ss");
 
      }
      
      // CompExpireDate: Optional, only add if provided
      if (companyData.compExpireDate && isValidDate(companyData.compExpireDate)) {
        payload.CompExpireDate = moment(companyData.compExpireDate).format("YYYY-MM-DDTHH:mm:ss");
 
      }
      
      // Hidden date fields: Always include with current date to avoid DateTime.MinValue (1/1/0001) SQL errors
      payload.MobileAppExpireDate = companyData.mobileAppExpireDate 
        ? moment(companyData.mobileAppExpireDate).format("YYYY-MM-DDTHH:mm:ss")
        : moment().format("YYYY-MM-DDTHH:mm:ss");
 
      
      payload.WebAppExpireDate = companyData.webAppExpireDate 
        ? moment(companyData.webAppExpireDate).format("YYYY-MM-DDTHH:mm:ss")
        : moment().format("YYYY-MM-DDTHH:mm:ss");
 
      
      payload.WaiterAppExpireDate = companyData.waiterAppExpireDate 
        ? moment(companyData.waiterAppExpireDate).format("YYYY-MM-DDTHH:mm:ss")
        : moment().format("YYYY-MM-DDTHH:mm:ss");
  
      // Remove any null, undefined, or empty string values that might cause issues
      const preserveWhenEmpty = ["CompType"];
      Object.keys(payload).forEach(key => {
        if (preserveWhenEmpty.includes(key)) return;
        if (payload[key] === null || payload[key] === undefined || payload[key] === "") {
          console.log(`Removing empty field: ${key} = ${payload[key]}`);
          delete payload[key];
        }
      });
  
      const response = await axiosInstance.post(APIs.POST_ADD_COMPANY, payload, {
        timeout: 120000,
      });

      if (response.data.status) {
        const responseData = response.data?.data || {};
        const responseCompany = responseData?.Company || responseData?.company || {};
        const companyPinValue =
          responseData?.CompanyPin ??
          responseData?.companyPin ??
          responseCompany?.CompSecCode ??
          responseCompany?.compSecCode ??
          companyData.compSecCode ??
          "";
        const staffPinValue =
          responseData?.StaffPin ??
          responseData?.staffPin ??
          responseData?.StaffPasscode ??
          responseData?.staffPasscode ??
          responseCompany?.StaffPin ??
          responseCompany?.staffPin ??
          "";
        const backOfficeUserNameValue =
          responseData?.BackOfficeUserName ??
          responseData?.backOfficeUserName ??
          responseCompany?.CompEmail ??
          responseCompany?.compEmail ??
          companyData.compEmail ??
          "";
        const backOfficePasswordValue =
          responseData?.BackOfficePassword ??
          responseData?.backOfficePassword ??
          responseData?.AdminPassword ??
          responseData?.adminPassword ??
          "";

        setCreatedCredentials({
          companyPin: companyPinValue,
          staffPin: staffPinValue,
          backOfficeUserName: backOfficeUserNameValue,
          backOfficePassword: backOfficePasswordValue,
        });
        setShowCredentialsModal(true);
        showSuccessToast(response.data.message || "Company added successfully!");
      } else {
        showErrorToast(response.data.message || "Failed to add company. Please check all fields.");
      }
    } catch (error) {
      console.error("Error adding company:", error);
      
      // Handle different error types
      if (error.response?.status === 404) {
        showErrorToast("Add company endpoint not found. Please check server configuration.");
      } else if (error.response?.status === 400) {
        // Validation error
        const errorData = error.response.data;
        if (errorData?.errors) {
          // Extract first error message
          const firstError = Object.values(errorData.errors)[0];
          const errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
          showErrorToast(errorMessage || "Validation error. Please check all fields.");
        } else if (errorData?.title) {
          showErrorToast(errorData.title);
        } else if (errorData?.message) {
          showErrorToast(errorData.message);
        } else {
          showErrorToast("Invalid data. Please check all required fields.");
        }
      } else if (error.response?.data?.message) {
        showErrorToast(error.response.data.message);
      } else if (error.response?.data?.title) {
        showErrorToast(error.response.data.title);
      } else if (error.message) {
        showErrorToast(`Error: ${error.message}`);
      } else {
        showErrorToast("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-2 mt-2">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/company/manageCompany">Manage Company</Link>
            </li>
            <li className="breadcrumb-item active">Add Company</li>
          </ol>
          <h1 className="page-header mb-0">Add New Company</h1>
        </div>
        <div className="ms-auto">
          <button
            onClick={handleSubmit}
            className="btn btn-success btn-rounded px-4 rounded-pill"
            disabled={loading}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-success-900"></i>
            Submit
          </button>
        </div>
      </div>

      <Panel>
        <PanelHeader>General</PanelHeader>
        <PanelBody>
          <div className="row mb-3">
            <div className="col-6">
              <FormInput
                label="Company Name"
                placeHolder="Enter Company Name"
                type="text"
                value={companyData.compName}
                onchange={(e) => handleInputChange(e, "compName")}
              />
            </div>
            <div className="col-6">
              <FormInput
                label="Email"
                placeHolder="Enter Email Address"
                type="email"
                value={companyData.compEmail}
                onchange={(e) => handleInputChange(e, "compEmail")}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <FormInput
                label="Phone Number"
                placeHolder="Enter Phone Number"
                type="tel"
                value={companyData.compPhone}
                onchange={(e) => handleInputChange(e, "compPhone")}
              />
            </div>
            <div className="col-6">
              <FormInput
                label="Fax Number"
                placeHolder="Enter Fax Number"
                type="tel"
                value={companyData.compFax}
                onchange={(e) => handleInputChange(e, "compFax")}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <label>Industry Type</label>
              <Select
                value={selectedIndustryType}
                onChange={(option) => {
                  setSelectedIndustryType(option);
                  const nextIndustryType = option ? option.value : "";
                  setSelectedCompType(null);
                  setCompanyData({
                    ...companyData,
                    compIndustryType: nextIndustryType,
                    compType: "",
                  });
                }}
                options={industryTypes}
                styles={isDarkMode ? DropDownStylesDark : DropDownStyles}
                placeholder="Select Industry Type"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuShouldBlockScroll={false}
                menuShouldScrollIntoView={false}
                closeMenuOnScroll={false}
              />
            </div>
            <div className="col-6">
              <label>Currency</label>
              <Select
                value={selectedCurrency}
                onChange={(option) => {
                  setSelectedCurrency(option);
                  setCompanyData({
                    ...companyData,
                    compCurrency: option ? option.value : "",
                  });
                }}
                options={currencies}
                styles={isDarkMode ? DropDownStylesDark : DropDownStyles}
                placeholder="Select Currency"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuShouldBlockScroll={false}
                menuShouldScrollIntoView={false}
                closeMenuOnScroll={false}
              />
            </div>
          </div>

          {isHospitalityIndustry(companyData.compIndustryType) && (
            <div className="row mb-3">
              <div className="col-6">
                <label>Company Type</label>
                <Select
                  value={selectedCompType}
                  onChange={(option) => {
                    setSelectedCompType(option);
                    setCompanyData({
                      ...companyData,
                      compType: option ? option.value : "",
                    });
                  }}
                  options={HOSPITALITY_COMPANY_TYPES}
                  styles={isDarkMode ? DropDownStylesDark : DropDownStyles}
                  placeholder="Select Company Type"
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  closeMenuOnScroll={false}
                />
              </div>
            </div>
          )}

          <div className="row mb-3">
            <CountryCitySelector
              selectedCountry={selectedCountry}
              selectedCity={selectedCity}
              setSelectedCity={setSelectedCity}
              onChange={handleCountryCityChange}
              setLoading={setLoading}
              selectedValueIsLabel={true}
              isDarkMode={isDarkMode}
            />
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <FormInput
                label="Address"
                placeHolder="Enter Company Address"
                type="text"
                value={companyData.compAddress}
                onchange={(e) => handleInputChange(e, "compAddress")}
              />
            </div>
            <div className="col-6">
              <FormInput
                label="Code"
                placeHolder="Enter Company Code"
                type="text"
                value={companyData.compSecCode}
                onchange={(e) => handleInputChange(e, "compSecCode")}
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-6">
              <FormInput
                label="Latitude"
                placeHolder="Auto from map or type latitude"
                type="number"
                value={companyData.compLatitude}
                onchange={(e) => handleInputChange(e, "compLatitude")}
                step="0.000001"
              />
            </div>
            <div className="col-6">
              <FormInput
                label="Longitude"
                placeHolder="Auto from map or type longitude"
                type="number"
                value={companyData.compLongitude}
                onchange={(e) => handleInputChange(e, "compLongitude")}
                step="0.000001"
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-12">
              <CoordinatePicker
                latitude={companyData.compLatitude}
                longitude={companyData.compLongitude}
                addressHint={
                  [
                    companyData.compAddress,
                    companyData.CompCity || companyData.compCity,
                    companyData.CompCountry || companyData.compCountry,
                  ]
                    .filter(Boolean)
                    .join(", ")
                }
                onSelect={(lat, lng) =>
                  setCompanyData((prev) => ({
                    ...prev,
                    compLatitude: lat == null ? "0" : String(lat),
                    compLongitude: lng == null ? "0" : String(lng),
                  }))
                }
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <FormInput
                label="Push Notification Limit Per Day"
                placeHolder="Enter Push Notification Limit"
                type="number"
                value={companyData.compPNotificationLimit_PerDay}
                onchange={(e) =>
                  handleInputChange(e, "compPNotificationLimit_PerDay")
                }
                step="10"
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <h6>Remarks</h6>
              <textarea
                className="form-control"
                placeholder="Enter Company Remarks"
                value={companyData.compRemarks}
                onChange={(e) => handleInputChange(e, "compRemarks")}
                rows="3"
                style={{
                  borderRadius: "10px",
                  border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                  background: isDarkMode ? "#2d3436" : "#F9F9F9",
                  color: isDarkMode ? "#ffffff" : "#495057",
                }}
              />
            </div>
            <div className="col-6">
              <h6>Company Detail</h6>
              <textarea
                className="form-control"
                placeholder="Enter Company Detail"
                value={companyData.compDetail}
                onChange={(e) => handleInputChange(e, "compDetail")}
                rows="3"
                style={{
                  borderRadius: "10px",
                  border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                  background: isDarkMode ? "#2d3436" : "#F9F9F9",
                  color: isDarkMode ? "#ffffff" : "#495057",
                }}
              />
            </div>
          </div>
        </PanelBody>
      </Panel>


      <Panel>
        <PanelHeader>Licenses & Limits</PanelHeader>
        <PanelBody>
          <div className="row mb-3">
            <div className="col-4">
              <FormInput
                label="Device License"
                placeHolder="Enter Device License"
                type="number"
                value={companyData.compMaxIPadLicense}
                onchange={(e) => handleInputChange(e, "compMaxIPadLicense")}
                step="1"
              />
            </div>
            <div className="col-4">
              <FormInput
                label="Max User License"
                placeHolder="Enter Max User License"
                type="number"
                value={companyData.compMaxUserLicense}
                onchange={(e) => handleInputChange(e, "compMaxUserLicense")}
                step="1"
              />
            </div>
            <div className="col-4">
              <FormInput
                label="Location Licenses"
                placeHolder="Enter Location Licenses"
                type="number"
                value={companyData.locationLicences}
                onchange={(e) => handleInputChange(e, "locationLicences")}
                step="1"
              />
            </div>
          </div>

        </PanelBody>
      </Panel>

      <Panel>
        <PanelHeader>Dates & Settings</PanelHeader>
        <PanelBody>
          <div className="row mb-3">
            <div className="col-6">
              <CustomDateTimePicker
                label="Registration Start Date"
                value={companyData.compRegDate}
                onChange={(newValue) =>
                  handleDateChange(newValue, "compRegDate")
                }
                mode="date"
                placeholder="Select Date"
              />
            </div>
            <div className="col-6">
              <CustomDateTimePicker
                label="Registration End Date"
                value={companyData.compExpireDate}
                onChange={(newValue) =>
                  handleDateChange(newValue, "compExpireDate")
                }
                mode="date"
                placeholder="Select Date"
              />
            </div>
          </div>

          <div className="row mb-3 mt-3">
            <div className="col-4">
              <ToggleSwitch
                isChecked={companyData.compPublish}
                onToggle={(e) => handleInputChange(e, "compPublish")}
                label="Publish Company"
                name="compPublish"
              />
            </div>
            <div className="col-4">
              <ToggleSwitch
                isChecked={companyData.isWebApp}
                onToggle={(e) => handleInputChange(e, "isWebApp")}
                label="Go Fatafat"
                name="isWebApp"
              />
            </div>
            <div className="col-4">
              <ToggleSwitch
                isChecked={companyData.allowPointOfSale !== false}
                onToggle={(e) => handleInputChange(e, "allowPointOfSale")}
                label="Point of Sale"
                name="allowPointOfSale"
              />
            </div>
          </div>

          {companyData.isWebApp && (
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">Go Fatafat commission %</label>
                <input
                  type="number"
                  className="form-control"
                  min="0"
                  max="100"
                  step="0.01"
                  name="marketplace_PlatformCommissionPercent"
                  value={companyData.marketplace_PlatformCommissionPercent ?? 0}
                  onChange={(e) => handleInputChange(e, "marketplace_PlatformCommissionPercent")}
                />
                <small className="text-muted">Percent of food subtotal (ex tip/delivery).</small>
              </div>
              <div className="col-md-4">
                <label className="form-label">Go Fatafat fixed fee / order</label>
                <input
                  type="number"
                  className="form-control"
                  min="0"
                  step="0.01"
                  name="marketplace_PlatformCommissionFixedFee"
                  value={companyData.marketplace_PlatformCommissionFixedFee ?? 0}
                  onChange={(e) => handleInputChange(e, "marketplace_PlatformCommissionFixedFee")}
                />
                <small className="text-muted">Optional flat fee added per Go Fatafat order.</small>
              </div>
              <div className="col-md-4">
                <label className="form-label">Go Fatafat zone</label>
                <Select
                  options={zoneOptions}
                  value={selectedMarketplaceZone}
                  onChange={(opt) => {
                    setSelectedMarketplaceZone(opt);
                    setCompanyData((prev) => ({
                      ...prev,
                      marketplaceZone_ID: opt?.value || null,
                    }));
                  }}
                  styles={zoneSelectStyles}
                  isClearable
                  placeholder="Select service zone"
                />
                <small className="text-muted">
                  Required for rider offers (must match rider active shift zone).
                </small>
              </div>
            </div>
          )}
        </PanelBody>
      </Panel>

      <CustomModal
        isOpen={showCredentialsModal}
        onClose={() => {
          setShowCredentialsModal(false);
          navigate("/company/manageCompany");
        }}
        title="Company Created Successfully"
        subtitle="Please save and share these credentials. The same details are sent by email."
        showButton={true}
        showCancelButton={false}
        showSubmitButton={true}
        submitButtonText="Done"
        onSubmit={() => {
          setShowCredentialsModal(false);
          navigate("/company/manageCompany");
        }}
        modalSize="modal-md"
        isCentered={true}
      >
        <div className="mb-2">
          <div className="row mb-2">
            <div className="col-5 fw-bold">Company Pin:</div>
            <div className="col-7">{createdCredentials.companyPin || "-"}</div>
          </div>
          <div className="row mb-2">
            <div className="col-5 fw-bold">Staff Pin:</div>
            <div className="col-7">{createdCredentials.staffPin || "-"}</div>
          </div>
          <div className="row mb-2">
            <div className="col-5 fw-bold">Backoffice Username:</div>
            <div className="col-7">{createdCredentials.backOfficeUserName || "-"}</div>
          </div>
          <div className="row mb-2">
            <div className="col-5 fw-bold">Backoffice Password:</div>
            <div className="col-7">{createdCredentials.backOfficePassword || "-"}</div>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}

export default AddCompany;